<script setup>
import Swal from 'sweetalert2';
import axios from 'axios';
import WhitelabelOption from "./WhitelabelOption";
import {computed, onMounted, ref} from "vue";
import FeatureComparison from "@/components/whitelabel/FeatureComparison.vue";

defineProps({
  hasActiveMerchantCustomers: {
    type: Boolean,
    required: true,
  },
});

const agencyPlus = ref(null);
const loading = ref(false);

onMounted(() => {
  fetchAgencyProducts();
});

const headerTitle = computed(() => {
  return 'Manage your Hike agency subscription';
});

const headerDescription = computed(() => {
  return 'Here you can manage your Hike subscription between the Agency Growth plan (included for all users) and the Agency Plus plan (optional upgrade) which gives you access to our full suite of agency features.';
});

const cancelWhitelabelPlusButtonText = computed(() => {
  if (agencyPlus.value?.owned) {
    return 'Downgrade to Agency Growth plan';
  }

  return null;
});

const fetchAgencyProducts = async () => {
  const { data } = await axios.get('/agency-plus/product');

  agencyPlus.value = data.agency_plus;
}

const purchase = () => {
  loading.value = true;

  window.addonPurchaser.purchaseAgencyPlus().then(() => {
    loading.value = false;
  });
}

const cancelProduct = (product) => {
  loading.value = true;

  Swal.fire({
    icon: "warning",
    title: "Are you sure?",
    text: `Cancelling ${product.name} will result in reduced functionality and loss of access to some features.`,
    confirmButtonColor: '#B23737',
    showCancelButton: true,
    showLoaderOnConfirm: true,
    backdrop: true,
    preConfirm: async () => {
      return await window.addonPurchaser.purchaseByUrl('/purchase-addon/cancel-whitelabel')
    },
    allowOutsideClick: () => !Swal.isLoading()
  }).then(() => {
    loading.value = false;
    window.location.reload();
  }).catch(error => {
    const serverError = {
      message: 'Something went wrong',
      errors: ['Sorry, it looks like we cannot complete your request right now. Please try again.']
    };

    const errors = error.status === 500 ? serverError : (JSON.parse(error.responseText))

    Swal.fire({
      icon: 'error',
      title: errors.message,
      html: errors.errors[0],
    }).then(() => loading.value = false)
  });
}
</script>

<template>
  <div
    v-if="agencyPlus"
    class="upgrade-to-whitelabel"
  >
    <section
      class="upgrade-options no-banner"
    >
      <div class="container">
        <h3
          class="text-center"
          v-html="headerTitle"
        />
        <p
          class="text-center"
          v-html="headerDescription"
        />

        <div
          v-if="hasActiveMerchantCustomers"
          class="disabled-message text-center mb-3"
        >
          <span class="text-bold">Please note:</span> You cannot cancel/downgrade your Agency Plus subscription while
          you have active merchant customers. Ensure all merchant customers are cancelled before proceeding.
        </div>

        <div
          v-if="agencyPlus"
          class="whitelabel-options"
        >
          <whitelabel-option
            :price="agencyPlus"
            :cancel-button="cancelWhitelabelPlusButtonText"
            :loading="loading"
            :disabled="hasActiveMerchantCustomers"
            @cancel="cancelProduct"
            @purchase="purchase"
          />
        </div>
      </div>
    </section>

    <FeatureComparison agency-growth-plan />
  </div>
</template>

<style lang="scss" scoped>
.upgrade-to-whitelabel {
  width: 100vw;

  header {
    background: #F8F7F5;
    padding: 6rem 0;

    h1,
    h3,
    p {
      max-width: 585px;
      margin: 0 auto;
    }

    h1 {
      font-weight: 700;
      font-size: 3rem;
      line-height: 3.75rem;
      text-align: center;
      letter-spacing: -2px;
      margin-bottom: 1rem;
    }

    h3 {
      font-size: 0.75rem;
      font-weight: 500;
      line-height: 1rem;
      text-align: center;
      letter-spacing: 3px;
      text-transform: uppercase;
      margin-bottom: 2rem;
    }

    .video_preview {
      filter: drop-shadow(0px 0px 2rem rgba(0, 0, 0, 0.2));
      margin: 4rem auto 0 auto;

      .top_bar {
        height: 2rem;
        background: #070606;
        width: 100%;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
        display: flex;
        padding-top: 10px;
        padding-left: 10px;

        .circle {
          width: 13px;
          height: 13px;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 100%;
          margin-right: 9px;
        }
      }
    }
  }

  section {
    background: #fff;

    &.upgrade-title {
      padding: 6rem 0;
      text-align: center;

      h3 {
        font-style: normal;
        font-weight: 500;
        font-size: 2rem;
        line-height: 2.5rem;
      }

      p {
        margin: 0;
      }
    }
  }

  p {
    color: #4E4E54;
  }
}

.upgrade-options {
  padding: 4rem 0;
  background: #F8F7F5;

  &.no-banner {
    margin-top: 2rem;
  }

  h3 {
    font-style: normal;
    font-weight: 500;
    font-size: 2rem;
    line-height: 2.5rem;
  }

  p {
    margin: 0 0 2rem 0;
  }
}

.upgrade-reasons {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2.313rem;
  padding: 0 0 4rem 0;

  .reason {
    text-align: center;
    padding: 0 2rem;

    img {
      margin-bottom: 1rem;
      max-width: 100%;
    }

    h4 {
      font-weight: 700;
      font-size: 1.5rem;
      line-height: 2.125rem;
    }
  }
}

@media screen and (max-width: 767px) {
  .upgrade-reasons {
    grid-template-columns: repeat(1, 1fr);
  }
}

.whitelabel-options {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0;
  gap: 40px;

  @media screen and (max-width: 767px) {
    flex-direction: column;
  }
}

.disabled-message {
  color: red;
}
</style>
